import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { Plan } from '@alltrails/types/Plan';
import { logError, executeRecaptcha } from '@alltrails/core';
import Image from 'next/image';
import { ApiError } from '@alltrails/core/utils/api';
import { RecurlyError } from '@recurly/recurly-js';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import classNames from 'classnames';
import type Coupon from '@alltrails/types/Coupon';
import type ProductType from '@alltrails/amplitude/enums/ProductType';
import type WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { getPriceTotal } from '../../utils/plan';
import type ProResponse from '../../types/ProResponse';
import payPalLogoSrc from './paypal-logo.svg';
import whitePayPalLogoSrc from './white-paypal-logo.svg';
import styles from './PayPalButton.module.scss';

type Props = {
  coupon?: Coupon;
  onPayPalToken: ({ paypal_token, recaptcha }: { paypal_token: string; recaptcha: string }) => void;
  plan: Plan;
  setErrors: (errors: ProResponse['errors']) => void;
  darkVariant?: boolean;
  roundedCorners?: boolean;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
  showContinueWith?: boolean;
  productType: ProductType;
  purchaseTier: PurchaseTier;
  webProPurchaseLocation: WebProPurchaseLocation;
};

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1
export default function PayPalButton({
  coupon,
  onPayPalToken,
  plan,
  roundedCorners,
  darkVariant,
  setErrors,
  submitting,
  setSubmitting,
  showContinueWith,
  productType,
  purchaseTier,
  webProPurchaseLocation
}: Props) {
  const intl = useIntl();
  useEffect(() => {
    recurly.configure(process.env.NEXT_PUBLIC_RECURLY_KEY);
  }, []);

  function handleClick() {
    logPurchaseButtonTapped({ payment_method: 'paypal', product_type: productType, web_purchase_location: webProPurchaseLocation });

    const payPal = recurly.PayPal({
      display: {
        displayName: purchaseTier === 'peak' ? 'AllTrails Peak' : 'AllTrails Plus',
        // @ts-ignore - this is valid https://github.com/recurly/recurly-js/blob/4d702a0e6b514441cb3a3b41d528e214ab39df29/lib/recurly/paypal/strategy/direct.js#L16
        amount: String(getPriceTotal(plan, coupon))
      }
    });

    payPal.on('error', (error: RecurlyError) => {
      logError(error);
      logPurchaseFailed({ error_code: error.message, payment_method: 'paypal' });
    });

    payPal.on('token', async token => {
      try {
        setSubmitting(true);

        const recaptcha = await executeRecaptcha('userPaymentAction');

        await onPayPalToken({ paypal_token: token.id, recaptcha });
      } catch (e) {
        logPurchaseFailed({
          ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
          payment_method: 'paypal'
        });

        if (e instanceof ApiError) {
          setErrors(e.data);
        } else {
          setErrors({ base: [] });
        }
        setSubmitting(false);
      }
    });

    payPal.start();
  }

  return (
    <button
      {...(!showContinueWith ? { 'aria-label': intl.formatMessage({ defaultMessage: 'Pay with PayPal' }) } : {})}
      className={classNames(styles.button, { [styles.roundedCorners]: roundedCorners, [styles.darkVariant]: darkVariant })}
      disabled={submitting}
      onClick={handleClick}
      type="button"
      name="paypal-button"
    >
      {showContinueWith && (
        <span className={styles.text}>
          <FormattedMessage defaultMessage="Continue with" />
          &nbsp;
        </span>
      )}
      <span className={styles.logo}>
        <Image alt="" height="18" src={darkVariant ? whitePayPalLogoSrc : payPalLogoSrc} />
      </span>
    </button>
  );
}
